import {Image} from '@shopify/hydrogen';
import {useEffect, useState, useCallback} from 'react';
import {AnimatePresence} from 'framer-motion';
import useEmblaCarousel, {EmblaCarouselType} from 'embla-carousel-react';
import type {GalleryVideos, ProductWithNodes} from '../../types';
import VideoPopup from '../ui/VideoPopup.client';
import clsx from 'clsx';

import type {MediaImage} from '@shopify/hydrogen/storefront-api-types';

/**
 * A client component that defines a media gallery for hosting images, 3D models, and videos of products
 */

type YouTubeMedia = {
  mediaContentType: 'YOUTUBE';
  videoId: string;
  start?: number;
  end?: number;
};

type GalleryMedia = Array<MediaImage | YouTubeMedia>;

type Props = {
  storefrontProduct: ProductWithNodes;
  galleryVideos?: GalleryVideos;
  featureVideo?: string;
};

export default function ProductGallery({
  storefrontProduct,
  featureVideo,
  galleryVideos,
}: Props) {
  const media = storefrontProduct?.media?.nodes;
  if (!media?.length) return null;

  const [featuredVideoIsOpen, setFeaturedVideoIsOpen] =
    useState<boolean>(false);
  const [galleryVideoIsOpen, setGalleryVideoIsOpen] = useState<boolean>(false);
  const [galleryVideoUrl, setGalleryVideoUrl] = useState<string>('');
  const [galleryMedia, setGalleryMedia] = useState<GalleryMedia>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    let gallery: GalleryMedia = [];

    if (!!galleryVideos?.length) {
      for (const video of galleryVideos) {
        gallery.push({
          mediaContentType: 'YOUTUBE',
          videoId: video.videoId,
          start: video.start,
          end: video.end,
        } as YouTubeMedia);
      }
    }

    gallery.push(
      ...media
        .filter((m) => m.mediaContentType === 'IMAGE')
        .map((m) => m as MediaImage),
    );

    setGalleryMedia(gallery);
  }, []);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'center',
    draggable: galleryMedia && galleryMedia.length > 1,
    loop: true,
    skipSnaps: true,
    speed: 7,
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi, galleryMedia]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect, galleryMedia]);

  return (
    <>
      <div className="top-space-s col-start-2 col-end-14 w-full items-center lg:sticky lg:col-end-8 ">
        {/* Gallery */}
        <div
          className="relative col-start-1 col-end-15 lg:col-start-1 lg:col-end-9 "
          tabIndex={-1}
        >
          <div
            className="relative h-full w-full overflow-hidden"
            ref={emblaRef}
          >
            <div className="flex items-center pt-6 sm:pt-0">
              {/* Slides */}
              {galleryMedia?.map((med, i) => {
                return (
                  <div
                    key={i}
                    className="relative flex h-full w-full"
                    style={{flex: '0 0 auto'}}
                  >
                    <div className="relative mx-auto h-full max-h-[600px] w-full max-w-[600px] overflow-hidden rounded">
                      {med.mediaContentType === 'YOUTUBE' ? (
                        <YouTubeSlide
                          key={med.videoId}
                          videoId={med.videoId}
                          start={med.start}
                          end={med.end}
                          setVideoUrl={setGalleryVideoUrl}
                          setVideoIsOpen={setGalleryVideoIsOpen}
                        />
                      ) : (
                        <PhotoSlide
                          src={med.image?.url}
                          alt={med.image?.altText}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Navigation */}
          {!!galleryMedia.length && (
            <Navigation
              media={galleryMedia}
              emblaApi={emblaApi}
              selectedIndex={selectedIndex}
            />
          )}

          {featureVideo && (
            <div className=" mt-6 w-full text-center">
              <button
                onClick={() => setFeaturedVideoIsOpen(true)}
                className="blockH8 mx-auto flex items-center justify-center gap-x-4 text-orange underline"
              >
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange/30 transition-all hover:scale-110">
                  <svg
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M22.3126 12.6875C21.9507 15.8544 17.6116 21.6875 11.8126 21.6875C6.37509 21.3125 2.25006 17 1.50009 12.125C0.559267 6.00945 5.4511 3.3125 11.2501 3.3125C16.3126 4.4375 23.0626 6.125 22.3126 12.6875Z"
                      fill="#FEF1E9"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M4.29647 4.39619C8.02904 2.02137 11.6211 2.06642 15.6192 2.87581C15.9082 2.93432 16.0946 3.21364 16.0355 3.4997C15.9764 3.78575 15.6942 3.97021 15.4051 3.9117C15.0805 3.84598 14.7601 3.78574 14.4436 3.73179C16.4887 4.28604 18.6424 5.04186 20.2757 6.21314C21.3795 7.00471 22.2751 8.00595 22.7269 9.28885C23.1802 10.5759 23.1654 12.0852 22.5581 13.8506C21.3347 17.4075 18.6163 20.2491 15.4004 21.5236C12.1647 22.806 8.43099 22.4948 5.27954 19.7482C3.16782 17.9077 1.48952 15.0297 1.03793 12.144C0.582961 9.23671 1.37338 6.25598 4.29647 4.39619ZM11.8716 3.44514C9.44627 3.34478 7.19152 3.8112 4.87417 5.2856C2.39436 6.86336 1.68567 9.375 2.09367 11.9821C2.50505 14.6109 4.05182 17.2693 5.98558 18.9547C8.81062 21.4168 12.1111 21.6883 15.0033 20.5421C17.9153 19.388 20.4187 16.7896 21.5468 13.5098C22.0942 11.9184 22.0771 10.6562 21.7181 9.63688C21.3577 8.61344 20.6308 7.77358 19.6488 7.06934C17.6619 5.64442 14.7523 4.85733 12.1888 4.26038C11.9017 4.1935 11.7236 3.90889 11.7912 3.62468C11.807 3.55838 11.8347 3.49797 11.8716 3.44514Z"
                      fill="#F27021"
                    />
                    <path
                      d="M13.6212 10.8095C13.0533 10.5626 12.4853 10.291 11.941 10.0194C10.9945 9.57501 10.0715 9.10588 9.1013 8.73552C9.05397 8.71082 9.00665 8.68613 8.95932 8.68613C8.77 8.63675 8.62802 8.71082 8.53336 8.85897C8.13106 9.15526 8.15473 9.5997 8.17839 10.1676C8.20206 11.575 8.20206 12.9823 8.27305 14.3897C8.36771 16.9082 10.8761 15.1551 12.0357 14.4391C12.5563 14.1181 13.0769 13.7971 13.5975 13.4515C14.0235 13.1799 14.6151 12.9823 14.9464 12.5873C15.3014 12.1922 15.2777 11.7478 14.8991 11.3774C14.5678 11.0811 13.9998 10.9824 13.6212 10.8095Z"
                      fill="#F27021"
                    />
                  </svg>
                </div>
                <span>See how it works</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {featuredVideoIsOpen && !galleryVideoIsOpen && (
          <VideoPopup
            key={'featuredVideo'}
            isGalleryPlayer
            url={featureVideo || ''}
            setIsOpen={setFeaturedVideoIsOpen}
          />
        )}
        {galleryVideoIsOpen && galleryVideoUrl && !featuredVideoIsOpen && (
          <VideoPopup
            key={`galleryVideo-${galleryVideoUrl}`}
            isGalleryPlayer
            url={galleryVideoUrl}
            setIsOpen={setGalleryVideoIsOpen}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function PhotoSlide(props: {src?: string | null; alt?: string | null}) {
  if (!props.src) return null;

  return (
    <Image
      height={600}
      width={600}
      loading="eager"
      className="relative flex aspect-1 h-full w-full select-none object-cover"
      src={props.src}
      alt={props.alt || 'Product Image'}
      draggable={false}
      tabIndex={0}
    />
  );
}

function YouTubeSlide(props: {
  videoId: string;
  start?: number;
  end?: number;
  setVideoUrl: React.Dispatch<React.SetStateAction<string>>;
  setVideoIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  let embedUrl = `https://www.youtube.com/embed/${props.videoId}`;
  let params = `?playlist=${props.videoId}&controls=0&fs=0&loop=1&mute=1&color=white&iv_load_policy=3&autoplay=1`;

  if (props.start) {
    params += `&start=${props.start}`;
  }

  if (props.end) {
    params += `&end=${props.end}`;
  }

  function handlePlayVideo() {
    props.setVideoUrl(embedUrl);
    props.setVideoIsOpen(true);
  }

  return (
    <div className="relative aspect-1 h-full w-full">
      <div className="absolute left-0 top-0 h-full w-full">
        <iframe
          width="600"
          height="600"
          src={embedUrl + params}
          title="YouTube"
          allow="autoplay; encrypted-media;"
          className="ml-[-45%] h-full w-[190%]"
        ></iframe>
      </div>
      <div className="absolute left-0 top-0 z-20 h-full w-full bg-black/20" />
      <Image
        alt="Play video"
        src="/images/video-play.png"
        width={120}
        height={120}
        className="absolute left-1/2 top-1/2 z-30 h-[80px] w-[80px] -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all hover:scale-110 sm:h-[120px] sm:w-[120px]"
        onClick={handlePlayVideo}
      />
    </div>
  );
}

function Navigation({
  media,
  emblaApi,
  selectedIndex,
}: {
  emblaApi?: EmblaCarouselType;
  media: GalleryMedia;
  selectedIndex: number;
}) {
  const showArrows = media.length > 5;

  const handleNext = () => {
    if (!emblaApi || !embla) return;
    emblaApi.scrollNext();
    embla.scrollNext();
  };

  const handlePrev = () => {
    if (!emblaApi || !embla) return;
    emblaApi.scrollPrev();
    embla.scrollPrev();
  };

  const handleClick = (i: number) => {
    if (!emblaApi || !embla) return;
    emblaApi.scrollTo(i);
    embla.scrollTo(i);
  };

  const [navRef, embla] = useEmblaCarousel({
    align: 'start',
    loop: true,
    speed: 7,
    axis: 'x',
    draggable: false,
    active: showArrows,
  });

  //scroll to selected index
  useEffect(() => {
    if (emblaApi && embla) {
      emblaApi.scrollTo(emblaApi.selectedScrollSnap());
      embla.scrollTo(emblaApi.selectedScrollSnap());
    }
  }, [selectedIndex]);

  return (
    <div className="mx-auto mt-6 flex w-full max-w-[336px] items-center gap-x-4 md:w-fit md:max-w-full">
      {showArrows && (
        <button onClick={() => handlePrev()} className="flex justify-center">
          <svg
            width="18"
            height="12"
            viewBox="0 0 18 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="rotate-90"
          >
            <path
              d="M0.5 1.35992C0.5 1.11356 0.605402 0.869506 0.809547 0.699119C1.1741 0.395459 1.7162 0.444577 2.01981 0.809123L8.99979 9.18536L15.9798 0.809124C16.2834 0.444577 16.8255 0.39546 17.1906 0.69912C17.5551 1.00329 17.605 1.54434 17.3006 1.90991L9.66044 11.0786C9.49722 11.275 9.25522 11.3881 9.00042 11.3881C8.74537 11.3881 8.50362 11.2753 8.34041 11.0786L0.700291 1.90991C0.565472 1.74874 0.500235 1.55329 0.500235 1.35964L0.5 1.35992Z"
              fill="black"
            />
          </svg>
        </button>
      )}
      <div
        ref={navRef}
        className="relative h-full w-full overflow-hidden md:w-[400px]"
      >
        <div
          className={clsx(
            'flex h-full w-full gap-x-4',
            !showArrows && 'justify-center',
          )}
        >
          {media?.map((med, index) => {
            const isActive = selectedIndex === index;
            return (
              <div
                key={index}
                onClick={() => handleClick(index)}
                className={clsx(
                  'relative z-10 aspect-1 w-1/6 flex-shrink-0 cursor-pointer  md:w-1/6',
                  showArrows && 'last:mr-4',
                )}
              >
                <div
                  className={clsx(
                    'relative aspect-1 overflow-hidden rounded-sm',
                    isActive
                      ? 'border-2 border-orange opacity-100'
                      : 'opacity-50',
                  )}
                >
                  {med.mediaContentType === 'YOUTUBE' ? (
                    <div className="relative aspect-1 h-full w-full overflow-hidden rounded-sm">
                      <div className="absolute left-0 top-0 z-10 scale-150">
                        <PhotoSlide
                          src={`https://i.ytimg.com/vi/${med.videoId}/hqdefault.jpg`}
                          alt={'Play video'}
                        />
                      </div>
                      <Image
                        alt="Play video"
                        src="/images/video-play.png"
                        width={30}
                        height={30}
                        className="absolute left-1/2 top-1/2 z-20 h-[30px] w-[30px] -translate-x-1/2 -translate-y-1/2"
                      />
                    </div>
                  ) : (
                    <div className="aspect-1 overflow-hidden rounded-sm">
                      <PhotoSlide
                        src={med.image?.url}
                        alt={med.image?.altText}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {showArrows && (
        <button onClick={() => handleNext()} className="flex  justify-center">
          <svg
            width="18"
            height="12"
            viewBox="0 0 18 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="rotate-[270deg]"
          >
            <path
              d="M0.5 1.35992C0.5 1.11356 0.605402 0.869506 0.809547 0.699119C1.1741 0.395459 1.7162 0.444577 2.01981 0.809123L8.99979 9.18536L15.9798 0.809124C16.2834 0.444577 16.8255 0.39546 17.1906 0.69912C17.5551 1.00329 17.605 1.54434 17.3006 1.90991L9.66044 11.0786C9.49722 11.275 9.25522 11.3881 9.00042 11.3881C8.74537 11.3881 8.50362 11.2753 8.34041 11.0786L0.700291 1.90991C0.565472 1.74874 0.500235 1.55329 0.500235 1.35964L0.5 1.35992Z"
              fill="black"
            />
          </svg>
        </button>
      )}
    </div>
  );
}
